/* @jsx h */
import { defineComponent, h, ref, computed } from "vue";
import { useRoute, RouterLink } from "vue-router";
import { STUB_PROJECTS, STUB_AGENTS, getAgent } from "../stub-data";
import { AGENT_COLOR_MAP, PROJECT_COLOR_MAP } from "../renderers/index";
import { AgentAvatar } from "../components/AgentSidebar";
import type { R2Asset, Schedule, ArtifactRepo, ProjectMember, Project } from "../types";

const formatBytes = (bytes: number) =>
  bytes < 1024 ? `${bytes} B` : bytes < 1024 ** 2 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 ** 2).toFixed(1)} MB`;

const EXT_ICONS: Record<string, string> = {
  pdf: "picture_as_pdf",
  csv: "table_chart",
  jpg: "image",
  png: "image",
  other: "description",
};

const PropertyRow = defineComponent<{ icon: string; label: string }>({
  name: "PropertyRow",
  props: ["icon", "label"],
  setup(props, { slots }) {
    return () => (
      <div class="grid grid-cols-[140px_minmax(0,1fr)] items-start gap-4 border-b border-base-content/10 py-2.5 last:border-b-0 max-sm:grid-cols-1 max-sm:gap-1">
        <div class="flex items-center gap-2 text-sm text-base-content/40">
          <span class="material-symbols-outlined text-accent/70" style="font-size:16px">{props.icon}</span>
          {props.label}
        </div>
        <div class="min-w-0">{slots.default?.()}</div>
      </div>
    );
  },
});

const SectionTitle = defineComponent<{ title: string; description: string; actionIcon?: string; actionLabel?: string }>({
  name: "SectionTitle",
  props: ["title", "description", "actionIcon", "actionLabel"],
  setup(props) {
    return () => (
      <div class="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 class="text-base font-semibold text-accent">{props.title}</h2>
          <p class="mt-1 text-sm text-base-content/45">{props.description}</p>
        </div>
        {props.actionLabel && (
          <button class="btn btn-sm btn-ghost gap-1 text-base-content/55">
            {props.actionIcon && <span class="material-symbols-outlined" style="font-size:15px">{props.actionIcon}</span>}
            {props.actionLabel}
          </button>
        )}
      </div>
    );
  },
});

const AgentsSection = defineComponent<{ agentIds: string[] }>({
  name: "AgentsSection",
  props: ["agentIds"],
  setup(props) {
    const active = ref<Set<string>>(new Set(props.agentIds));

    return () => (
      <section class="mb-9">
        <SectionTitle title="Agents" description="Persistent colleagues available inside every thread in this project." actionIcon="add" actionLabel="Add agent" />
        <div class="overflow-hidden rounded-lg border border-base-content/10">
          {STUB_AGENTS.map((agent) => {
            const enabled = active.value.has(agent.id);
            const colors = AGENT_COLOR_MAP[agent.avatarColor];
            return (
              <div key={agent.id} class="grid grid-cols-[minmax(220px,1fr)_120px_120px] items-center gap-3 border-b border-base-content/10 px-3 py-2.5 last:border-b-0 max-md:grid-cols-[minmax(180px,1fr)_80px] max-md:[&_.optional-col]:hidden">
                <div class="flex min-w-0 items-center gap-2">
                  <AgentAvatar agent={agent} size="sm" />
                  <div class="min-w-0">
                    <p class={`truncate text-sm font-medium ${enabled ? colors.text : "text-base-content/45"}`}>{agent.name}</p>
                    <p class="truncate text-xs text-base-content/40">{agent.description}</p>
                  </div>
                </div>
                <span class={`w-fit rounded-md border px-1.5 py-0.5 text-xs ${enabled ? `${colors.bg} ${colors.border} ${colors.text}` : "border-base-content/10 bg-base-content/5 text-base-content/35"}`}>
                  {enabled ? "Enabled" : "Off"}
                </span>
                <div class="optional-col flex items-center justify-end gap-2">
                  <select disabled={!enabled} class="select select-xs select-ghost w-28 text-xs text-base-content/55">
                    <option>Default</option>
                    <option>Coordinator</option>
                  </select>
                  <input
                    type="checkbox"
                    class="toggle toggle-xs toggle-primary"
                    checked={enabled}
                    onChange={() => {
                      const next = new Set(active.value);
                      next.has(agent.id) ? next.delete(agent.id) : next.add(agent.id);
                      active.value = next;
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  },
});

const ROLE_CLASSES: Record<ProjectMember["role"], string> = {
  owner: "border-warning/25 bg-warning/10 text-warning",
  editor: "border-info/25 bg-info/10 text-info",
  viewer: "border-base-content/10 bg-base-content/5 text-base-content/45",
};

const TeamSection = defineComponent<{ members: ProjectMember[] }>({
  name: "TeamSection",
  props: ["members"],
  setup(props) {
    return () => (
      <section class="mb-9">
        <SectionTitle title="Team" description="People who can see this project, its files, and its threads." actionIcon="person_add" actionLabel="Invite" />
        <div class="overflow-hidden rounded-lg border border-base-content/10">
          {props.members.map((member) => (
            <div key={member.userId} class="grid grid-cols-[minmax(220px,1fr)_100px] items-center gap-3 border-b border-base-content/10 px-3 py-2.5 last:border-b-0">
              <div class="flex min-w-0 items-center gap-2">
                <div class="flex h-7 w-7 items-center justify-center rounded-md border border-base-content/10 bg-base-content/10 text-[10px] font-semibold text-base-content/55">
                  {member.initials}
                </div>
                <p class="truncate text-sm font-medium text-base-content">{member.name}</p>
              </div>
              <span class={`w-fit rounded-md border px-1.5 py-0.5 text-xs capitalize ${ROLE_CLASSES[member.role]}`}>{member.role}</span>
            </div>
          ))}
          <div class="flex items-center gap-2 px-3 py-2.5">
            <span class="material-symbols-outlined text-base-content/30" style="font-size:16px">person_add</span>
            <input type="text" placeholder="Invite by email or name..." class="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder-base-content/30" />
            <button class="btn btn-xs btn-primary">Invite</button>
          </div>
        </div>
      </section>
    );
  },
});

const FilesSection = defineComponent<{ assets: R2Asset[] }>({
  name: "FilesSection",
  props: ["assets"],
  setup(props) {
    return () => (
      <section class="mb-9">
        <SectionTitle title="Files" description="Source material agents can read, cite, and reuse." actionIcon="upload" actionLabel="Upload" />
        <div class="overflow-hidden rounded-lg border border-base-content/10">
          {props.assets.length === 0 ? (
            <div class="px-3 py-8 text-center text-sm text-base-content/35">No files yet.</div>
          ) : (
            props.assets.map((asset) => (
              <div key={asset.id} class="grid grid-cols-[minmax(220px,1fr)_90px_110px_32px] items-center gap-3 border-b border-base-content/10 px-3 py-2.5 last:border-b-0 max-md:grid-cols-[minmax(180px,1fr)_80px] max-md:[&_.optional-col]:hidden">
                <div class="flex min-w-0 items-center gap-2">
                  <span class="material-symbols-outlined text-base-content/35" style="font-size:18px">{EXT_ICONS[asset.ext] ?? EXT_ICONS.other}</span>
                  <p class="truncate text-sm text-base-content">{asset.name}</p>
                </div>
                <span class="text-xs text-base-content/45">{formatBytes(asset.sizeBytes)}</span>
                <span class="optional-col text-xs text-base-content/35">{new Date(asset.uploadedAt).toLocaleDateString()}</span>
                <button class="optional-col rounded-md p-1 text-base-content/30 hover:bg-error/10 hover:text-error">
                  <span class="material-symbols-outlined" style="font-size:16px">delete</span>
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    );
  },
});

const STUB_FILES = [
  { path: "index.html", size: "12 KB" },
  { path: "styles.css", size: "4 KB" },
  { path: "script.js", size: "8 KB" },
  { path: "assets/hero.jpg", size: "220 KB" },
  { path: "assets/logo.svg", size: "3 KB" },
];

const ArtifactSection = defineComponent<{ repo: ArtifactRepo | null }>({
  name: "ArtifactSection",
  props: ["repo"],
  setup(props) {
    return () => (
      <section class="mb-9">
        <SectionTitle title="Website" description="Versioned website repo managed by the Webdev agent." actionIcon={props.repo ? "preview" : "add"} actionLabel={props.repo ? "Preview" : "Create repo"} />
        {!props.repo ? (
          <div class="rounded-lg border border-dashed border-base-content/15 px-3 py-8 text-center text-sm text-base-content/35">No website repo connected.</div>
        ) : (
          <div class="rounded-lg border border-base-content/10 px-3 py-2">
            <PropertyRow icon="call_split" label="Branch">
              <span class="font-mono text-xs text-base-content/60">{props.repo.branch}</span>
            </PropertyRow>
            <PropertyRow icon="published_with_changes" label="Status">
              <span class={`rounded-md border px-1.5 py-0.5 text-xs ${props.repo.status === "deployed" ? "border-success/25 bg-success/10 text-success" : "border-warning/25 bg-warning/10 text-warning"}`}>
                {props.repo.status === "deployed" ? "Live" : "Draft"}
              </span>
            </PropertyRow>
            {props.repo.deployedUrl && (
              <PropertyRow icon="link" label="URL">
                <a href={props.repo.deployedUrl} target="_blank" rel="noopener noreferrer" class="text-sm text-primary hover:underline">{props.repo.deployedUrl}</a>
              </PropertyRow>
            )}
            <div class="mt-3 overflow-hidden rounded-md border border-base-content/10">
              {STUB_FILES.map((file) => (
                <div key={file.path} class="grid grid-cols-[minmax(0,1fr)_70px] items-center gap-3 border-b border-base-content/10 px-3 py-1.5 last:border-b-0">
                  <span class="truncate font-mono text-xs text-base-content/60">{file.path}</span>
                  <span class="text-xs text-base-content/35">{file.size}</span>
                </div>
              ))}
            </div>
            <div class="mt-3 flex flex-wrap gap-2">
              <button class="btn btn-sm btn-primary gap-1.5">
                <span class="material-symbols-outlined" style="font-size:15px">rocket_launch</span>
                Deploy
              </button>
              <a href={props.repo.deployedUrl ?? "#"} target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-ghost gap-1.5">
                <span class="material-symbols-outlined" style="font-size:15px">open_in_new</span>
                Open
              </a>
            </div>
          </div>
        )}
      </section>
    );
  },
});

const SchedulesSection = defineComponent<{ schedules: Schedule[] }>({
  name: "SchedulesSection",
  props: ["schedules"],
  setup(props) {
    const enabled = ref<Record<string, boolean>>(Object.fromEntries(props.schedules.map((schedule) => [schedule.id, schedule.enabled])));

    return () => (
      <section class="mb-9">
        <SectionTitle title="Schedules" description="Recurring agent work such as briefings, syncs, and checks." actionIcon="add" actionLabel="Add schedule" />
        <div class="overflow-hidden rounded-lg border border-base-content/10">
          {props.schedules.length === 0 ? (
            <div class="px-3 py-8 text-center text-sm text-base-content/35">No schedules yet.</div>
          ) : (
            props.schedules.map((schedule) => {
              const agent = schedule.agentId ? getAgent(schedule.agentId) : null;
              return (
                <div key={schedule.id} class="grid grid-cols-[minmax(220px,1fr)_110px_90px] items-center gap-3 border-b border-base-content/10 px-3 py-2.5 last:border-b-0 max-md:grid-cols-[minmax(180px,1fr)_70px] max-md:[&_.optional-col]:hidden">
                  <div class="flex min-w-0 items-center gap-2">
                    {agent ? <AgentAvatar agent={agent} size="sm" /> : <span class="material-symbols-outlined text-base-content/35" style="font-size:18px">schedule</span>}
                    <div class="min-w-0">
                      <p class="truncate text-sm font-medium text-base-content">{schedule.name}</p>
                      <p class="truncate text-xs text-base-content/40">{schedule.description}</p>
                    </div>
                  </div>
                  <span class="optional-col font-mono text-xs text-base-content/45">{schedule.cron}</span>
                  <div class="flex items-center justify-end gap-2">
                    <button class="optional-col rounded-md p-1 text-base-content/35 hover:bg-base-content/5 hover:text-base-content/70">
                      <span class="material-symbols-outlined" style="font-size:16px">play_arrow</span>
                    </button>
                    <input
                      type="checkbox"
                      class="toggle toggle-xs toggle-primary"
                      checked={enabled.value[schedule.id]}
                      onChange={() => {
                        enabled.value[schedule.id] = !enabled.value[schedule.id];
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>
    );
  },
});

export const ProjectBindingsPanel = defineComponent<{ project: Project }>({
  name: "ProjectBindingsPanel",
  props: ["project"],
  setup(props) {
    return () => {
      const { r2Assets, artifactRepo, schedules } = props.project.bindings;

      return (
        <div>
          <div class="mb-9 rounded-lg border border-base-content/10 px-3 py-2">
            <PropertyRow icon="smart_toy" label="Agents">
              <span class="text-sm text-base-content/65">{props.project.agentIds.length} enabled</span>
            </PropertyRow>
            <PropertyRow icon="group" label="Team">
              <span class="text-sm text-base-content/65">{props.project.members.length} member{props.project.members.length === 1 ? "" : "s"}</span>
            </PropertyRow>
            <PropertyRow icon="folder" label="Files">
              <span class="text-sm text-base-content/65">{r2Assets.length} source file{r2Assets.length === 1 ? "" : "s"}</span>
            </PropertyRow>
            <PropertyRow icon="schedule" label="Schedules">
              <span class="text-sm text-base-content/65">{schedules.length} automation{schedules.length === 1 ? "" : "s"}</span>
            </PropertyRow>
          </div>

          <AgentsSection agentIds={props.project.agentIds} />
          <TeamSection members={props.project.members} />
          <FilesSection assets={r2Assets} />
          <ArtifactSection repo={artifactRepo} />
          <SchedulesSection schedules={schedules} />
        </div>
      );
    };
  },
});

export const ProjectSettings = defineComponent({
  name: "ProjectSettings",
  setup() {
    const route = useRoute();
    const project = computed(() => STUB_PROJECTS.find((item) => item.id === route.params.projectId as string) ?? null);

    return () => {
      if (!project.value) {
        return (
          <div class="flex flex-1 items-center justify-center bg-base-100 text-base-content/35">
            <p class="text-sm">Project not found.</p>
          </div>
        );
      }

      const colors = PROJECT_COLOR_MAP[project.value.color] ?? PROJECT_COLOR_MAP.neutral;
      const backTo = project.value.threads[0] ? `/app/intelligence/projects/${project.value.id}/threads/${project.value.threads[0].id}` : "/app/intelligence";

      return (
        <main class="flex-1 overflow-y-auto bg-base-100">
          <div class="mx-auto w-full max-w-4xl px-8 py-8 max-md:px-4">
            <div class="mb-8">
              <div class="mb-4 flex items-center gap-2 text-sm text-base-content/45">
                <RouterLink to="/app/intelligence" class="text-accent hover:text-accent/80">Intelligence</RouterLink>
                <span class="material-symbols-outlined text-base-content/20" style="font-size:15px">chevron_right</span>
                <RouterLink to={backTo} class="hover:text-base-content">{project.value.name}</RouterLink>
                <span class="material-symbols-outlined text-base-content/20" style="font-size:15px">chevron_right</span>
                <span>Settings</span>
              </div>
              <div class={`mb-4 flex h-16 w-16 items-center justify-center rounded-lg border text-4xl ${colors.soft}`}>{project.value.icon}</div>
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <h1 class="text-3xl font-semibold tracking-normal text-base-content">
                    <span class="text-accent">{project.value.name}</span>
                  </h1>
                  <p class="mt-2 max-w-2xl text-sm leading-6 text-base-content/55">{project.value.description}</p>
                </div>
                <RouterLink to={backTo} class="btn btn-sm btn-ghost gap-1 text-base-content/55">
                  <span class="material-symbols-outlined" style="font-size:16px">arrow_back</span>
                  Back
                </RouterLink>
              </div>
            </div>

            <ProjectBindingsPanel project={project.value} />
          </div>
        </main>
      );
    };
  },
});
